import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { HelpLayout } from '@/components/help-center/help-layout'
import {
  detectDeviceFromUserAgent,
  deviceOptions,
  filterArticlesByDevice,
  getHelpPath,
  helpArticles,
  platformLabels,
  type DeviceKey,
} from '@/lib/help-center'

export const Route = createFileRoute('/_public/app/help/')({
  component: HelpIndexPage,
  head: () => ({
    meta: [
      { title: 'Help Center | MessageDrafts' },
      {
        name: 'description',
        content:
          'Browse guides for importing contacts, privacy details, and FAQ for MessageDrafts.',
      },
    ],
  }),
})

const storageKey = 'help-center-device'

function HelpIndexPage() {
  const [device, setDevice] = useState<DeviceKey>('all')
  const [needsSelection, setNeedsSelection] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem(storageKey)
    if (stored && isDeviceKey(stored)) {
      setDevice(stored)
      return
    }
    const detected = detectDeviceFromUserAgent()
    setDevice(detected)
    setNeedsSelection(detected === 'all')
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(storageKey, device)
  }, [device])

  const filteredArticles = useMemo(
    () => filterArticlesByDevice(helpArticles, device),
    [device],
  )

  return (
    <HelpLayout
      title="Get Help Fast"
      description="Find the right guide for your device, plus privacy details and FAQs."
    >
      <section className="grid gap-6 md:grid-cols-[1.1fr_0.9fr] items-start">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">
            Choose your device
          </h2>
          <p className="text-sm text-slate-600 mt-2">
            We use this to highlight the most relevant guides. You can change it anytime.
          </p>
          <div className="mt-4 max-w-sm">
            <Select
              value={device}
              onValueChange={(value) => {
                setDevice(value as DeviceKey)
                setNeedsSelection(false)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a device" />
              </SelectTrigger>
              <SelectContent>
                {deviceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 text-sm text-slate-600">
            {needsSelection ? (
              <span className="text-amber-700">
                We could not detect your device. Please choose one to personalize the guides.
              </span>
            ) : (
              <span>
                Showing guides for <strong>{platformLabels[device]}</strong>.
              </span>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold">Need help right now?</h2>
          <p className="text-sm text-slate-200 mt-2">
            Start with the import guide for your device, then review privacy details and FAQs.
          </p>
          <div className="mt-5 space-y-2">
            <p className="text-sm text-slate-300">Popular topics:</p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-white/10 text-white">Import contacts</Badge>
              <Badge className="bg-white/10 text-white">Privacy</Badge>
              <Badge className="bg-white/10 text-white">Troubleshooting</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-slate-900">Guides</h2>
          <span className="text-sm text-slate-500">
            {filteredArticles.length} articles
          </span>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {filteredArticles.map((article) => (
            <Link
              key={article.slug}
              to={getHelpPath(article.slug)}
              className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-slate-300"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 group-hover:text-slate-950">
                  {article.title}
                </h3>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {article.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {article.tags.slice(0, 4).map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </HelpLayout>
  )
}

function isDeviceKey(value: string): value is DeviceKey {
  return deviceOptions.some((option) => option.value === value)
}
